import { Table, PageHeader } from "antd";
import moment from "moment";
import { useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { createComponentStore, useStoreState } from "easy-peasy";
import { RULES } from "Constants/rules";
import {
  RealEstateReportStoreModel,
  ACTION_TYPES,
} from "store/models/reports/real-estate";
import ExportExcel from "Components/export-excel";
import DeedTypeSelect from "Components/deed-type-select";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import FourOThree from "Pages/private/403";
import { sort } from "Utils";

const useRealEstateReportsStore = createComponentStore(
  RealEstateReportStoreModel
);

export default function RealEstateReportPage() {
  const { abilities } = useStoreState((store) => store.auth);
  const IsAuthorized = AuthCheck(
    abilities,
    AUTH_ACTIONS.READ,
    "Taşınmaz Raporları"
  );

  const [store, actions] = useRealEstateReportsStore();
  const { fetch } = actions;
  const { process, data } = store;
  const [filteredValue, setFilteredValue] = useState({});

  const form = useForm({
    defaultValues: {
      deedType: null,
    },
  });

  const deedType = form.watch("deedType");

  useEffect(() => {
    fetch(filteredValue);
  }, [filteredValue]);

  useEffect(() => {
    if (deedType) setFilteredValue({ deedtypescode: deedType.code });
    else setFilteredValue({});
  }, [deedType]);

  const columns = [
    {
      title: "Kod",
      dataIndex: "realEstateCode",
      width: 80,
      key: "realEstateCode",
      sorter: (a, b) => a.realEstateCode.substr(1) - b.realEstateCode.substr(1),
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Takbis Zemin No",
      dataIndex: "takbisNumber",
      key: "takbisNumber",
      sorter: (a, b) => sort(a.takbisNumber, b.takbisNumber),
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Taşınmaz No",
      dataIndex: "realEstateNumber",
      key: "realEstateNumber",
      sorter: (a, b) => sort(a.realEstateNumber, b.realEstateNumber),
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "İlçe",
      dataIndex: "district",
      key: "district",
      width: 120,
      sorter: (a, b) => sort(a.district, b.district),
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Mahalle",
      dataIndex: "neighborhood",
      key: "neighborhood",
      sorter: (a, b) => sort(a.neighborhood, b.neighborhood),
      width: 120,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Ada No",
      dataIndex: "islandNumber",
      key: "islandNumber",
      sorter: (a, b) => sort(a.islandNumber, b.islandNumber),
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Parsel No",
      dataIndex: "parcelNumber",
      key: "parcelNumber",
      sorter: (a, b) => sort(a.parcelNumber, b.parcelNumber),
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Mevki",
      dataIndex: "locality",
      key: "locality",
      sorter: (a, b) => sort(a.locality, b.locality),
      width: 120,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Cadde",
      dataIndex: "avenue",
      key: "avenue",
      sorter: (a, b) => sort(a.avenue, b.avenue),
      width: 120,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Sokak",
      dataIndex: "street",
      key: "street",
      width: 120,
      sorter: (a, b) => sort(a.street, b.street),
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Cilt No",
      dataIndex: "vol",
      key: "vol",
      width: 100,
      sorter: (a, b) => sort(a.vol, b.vol),
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Pafta No",
      dataIndex: "sheetNumber",
      key: "sheetNumber",
      sorter: (a, b) => sort(a.sheetNumber, b.sheetNumber),
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Sıra No",
      dataIndex: "orderNumber",
      sorter: (a, b) => sort(a.orderNumber, b.orderNumber),
      key: "orderNumber",
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Yüzölçümü",
      dataIndex: "floorspace",
      key: "floorspace",
      sorter: (a, b) => sort(a.floorspace, b.floorspace),
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Pay Oranı",
      dataIndex: "shareRate",
      sorter: (a, b) => sort(a.shareRate, b.shareRate),
      key: "shareRate",
      width: 130,
      ellipsis: true,
      render: (text, record) => 100, //Bu alan şimdilik sabit 100 olarak gözükecek
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Taşınmaz Cinsi",
      dataIndex: "deedTypeDesc",
      key: "deedTypeDesc",
      sorter: (a, b) => sort(a.deedTypeDesc, b.deedTypeDesc),
      width: 110,
      ellipsis: true,
      render: (text, record) => record.deedTypeDesc || "",
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Mevcut Kullanım Şekli/Tahsis Amacı",
      dataIndex: "allocationPurposeDesc",
      key: "allocationPurposeDesc",
      sorter: (a, b) => sort(a.allocationPurposeDesc, b.allocationPurposeDesc),
      width: 110,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Edinme Şekli",
      sorter: (a, b) => sort(a.acqusFigDesc, b.acqusFigDesc),
      dataIndex: "acqusFigDesc",
      key: "acqusFigDesc",
      width: 120,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Edinme Tarihi",
      sorter: (a, b) => sort(a.dateOfAcquisition, b.dateOfAcquisition),
      dataIndex: "dateOfAcquisition",
      key: "dateOfAcquisition",
      width: 110,
      ellipsis: true,
      render: (text, record) => {
        return record.dateOfAcquisition
          ? moment(record.dateOfAcquisition).format("DD.MM.YYYY")
          : null;
      },
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Maliyet Bedeli",
      dataIndex: "costPrice",
      key: "costPrice",
      sorter: (a, b) => sort(a.costPrice, b.costPrice),
      width: 100,
      ellipsis: true,
      visibilityRules: [RULES.RULE0009, RULES.RULE0013],
    },

    {
      title: "Rayiç Bedeli",
      dataIndex: "marketValue",
      key: "marketValue",
      sorter: (a, b) => sort(a.marketValue, b.marketValue),
      width: 100,
      ellipsis: true,
      visibilityRules: [RULES.RULE0009, RULES.RULE0013],
    },

    {
      title: "Emlak Vergi Bedeli",
      dataIndex: "taxValue",
      key: "taxValue",
      sorter: (a, b) => sort(a.taxValue, b.taxValue),
      width: 100,
      ellipsis: true,
      visibilityRules: [RULES.RULE0009, RULES.RULE0013],
    },

    {
      title: "İz Bedeli",
      dataIndex: "tokenValue",
      key: "tokenValue",
      sorter: (a, b) => sort(a.tokenValue, b.tokenValue),
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Çıkış Nedeni",
      dataIndex: "outputShapeDesc",
      sorter: (a, b) => sort(a.outputShapeDesc, b.outputShapeDesc),
      key: "outputShapeDesc",
      width: 100,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "K. Çıkış Tarihi",
      dataIndex: "dateOfOutput",
      sorter: (a, b) => sort(a.dateOfOutput, b.dateOfOutput),
      key: "dateOfOutput",
      width: 110,
      ellipsis: true,
      render: (text, record) => {
        return record.dateOfOutput
          ? moment(record.dateOfOutput).format("DD.MM.YYYY")
          : null;
      },
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Açıklama",
      sorter: (a, b) => sort(a.realEstateDesc, b.realEstateDesc),
      dataIndex: "realEstateDesc",
      key: "realEstateDesc",
      width: 130,
      ellipsis: true,
      visibilityRules: [
        RULES.RULE0009,
        RULES.RULE0010,
        RULES.RULE0011,
        RULES.RULE0012,
        RULES.RULE0013,
      ],
    },

    {
      title: "Edinme Süresi",
      dataIndex: "durationOfAcquisition",
      key: "durationOfAcquisition",
      sorter: (a, b) => sort(a.durationOfAcquisition, b.durationOfAcquisition),
      width: 110,
      ellipsis: true,
    },

    {
      title: "Yönetim",
      dataIndex: "administration",
      sorter: (a, b) => sort(a.administration, b.administration),
      key: "administration",
      width: 100,
      ellipsis: true,
    },

    {
      title: "BKMYBS No",
      dataIndex: "bkmybsNumber",
      sorter: (a, b) => sort(a.bkmybsNumber, b.bkmybsNumber),
      key: "bkmybsNumber",
      width: 100,
      ellipsis: true,
    },

    {
      title: "BKMYBS Adı",
      dataIndex: "bkmybsName",
      key: "bkmybsName",
      sorter: (a, b) => sort(a.bkmybsName, b.bkmybsName),
      width: 100,
      ellipsis: true,
    },

    {
      title: "BKMYBS Sicil No",
      dataIndex: "bkmybsRegisterNumber",
      key: "bkmybsRegisterNumber",
      sorter: (a, b) => sort(a.bkmybsRegisterNumber, b.bkmybsRegisterNumber),
      width: 100,
      ellipsis: true,
    },

    {
      title: "BKMYBS Muhasebe Kodu",
      dataIndex: "bkmybsAccountingNumber",
      key: "bkmybsAccountingNumber",
      sorter: (a, b) =>
        sort(a.bkmybsAccountingNumber, b.bkmybsAccountingNumber),
      width: 100,
      ellipsis: true,
    },

    {
      title: "BKMYBS Giriş Tarihi",
      dataIndex: "bkmybsEntryDate",
      sorter: (a, b) => sort(a.bkmybsEntryDate, b.bkmybsEntryDate),
      key: "bkmybsEntryDate",
      width: 110,
      ellipsis: true,
      render: (text, record) => {
        return record.bkmybsEntryDate
          ? moment(record.bkmybsEntryDate).format("DD.MM.YYYY")
          : null;
      },
    },

    {
      title: "İl",
      dataIndex: "province",
      key: "province",
      width: 120,
      ellipsis: true,
      sorter: (a, b) => sort(a.province, b.province),
    },
    {
      title: "Kuzey",
      dataIndex: "north",
      key: "north",
      width: 100,
      ellipsis: true,
      sorter: (a, b) => sort(a.north, b.north),
    },

    {
      title: "Güney",
      dataIndex: "south",
      key: "south",
      width: 100,
      ellipsis: true,
      sorter: (a, b) => sort(a.south, b.south),
    },

    {
      title: "Doğu",
      dataIndex: "east",
      key: "east",
      width: 100,
      ellipsis: true,
      sorter: (a, b) => sort(a.east, b.east),
    },

    {
      title: "Batı",
      dataIndex: "west",
      key: "west",
      width: 100,
      ellipsis: true,
      sorter: (a, b) => sort(a.west, b.west),
    },

    {
      title: "Arşiv No",
      dataIndex: "archiveNumber",
      key: "archiveNumber",
      width: 100,
      ellipsis: true,
      sorter: (a, b) => sort(a.archiveNumber, b.archiveNumber),
    },

    {
      title: "Arşiv Tarihi",
      dataIndex: "dateOfArchive",
      key: "dateOfArchive",
      width: 110,
      ellipsis: true,
      sorter: (a, b) => sort(a.dateOfArchive, b.dateOfArchive),
      render: (text, record) => {
        return record.dateOfArchive
          ? moment(record.dateOfArchive).format("DD.MM.YYYY")
          : null;
      },
    },

    {
      title: "Kayıt Tarihi",
      dataIndex: "dateOfRegistration",
      key: "dateOfRegistration",
      width: 110,
      ellipsis: true,
      sorter: (a, b) => sort(a.dateOfRegistration, b.dateOfRegistration),
      render: (text, record) => {
        return record.dateOfRegistration
          ? moment(record.dateOfRegistration).format("DD.MM.YYYY")
          : null;
      },
    },
  ];

  const getModifiedColumns = () => {
    if (deedType) {
      return columns.filter((column) =>
        column?.visibilityRules?.includes(deedType.root?.rule?.code)
      );
    } else {
      return columns;
    }
  };

  const getExcelColumns = () => {
    return getModifiedColumns().map((d) => d.title);
  };

  const getExcelRows = () => {
    let rows = [];
    store.data.forEach((d) => {
      let i = [];
      i.push(d.realEstateCode || "-");

      if (
        !deedType ||
        [
          RULES.RULE0009,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(deedType.root?.rule?.code)
      )
        i.push(d.takbisNumber || "-");

      i.push(d.realEstateNumber || "-");
      i.push(d.district || "-");
      i.push(d.neighborhood || "-");

      if (
        !deedType ||
        [
          RULES.RULE0009,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(deedType.root?.rule?.code)
      ) {
        i.push(d.islandNumber || "-");
        i.push(d.parcelNumber || "-");
      }

      if (
        !deedType ||
        [
          RULES.RULE0010,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(deedType.root?.rule?.code)
      ) {
        i.push(d.locality || "-");
        i.push(d.avenue || "-");
        i.push(d.street || "-");
      }

      if (
        !deedType ||
        [
          RULES.RULE0009,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(deedType.root?.rule?.code)
      ) {
        i.push(d.vol || "-");
        i.push(d.sheetNumber || "-");
        i.push(d.orderNumber || "-");
      }

      i.push(d.floorspace || "-");

      if (
        !deedType ||
        [
          RULES.RULE0009,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(deedType.root?.rule?.code)
      ) {
        i.push("100");
      }

      i.push(d.deedTypeDesc || "-");

      if (
        !deedType ||
        [
          RULES.RULE0010,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(deedType.root?.rule?.code)
      ) {
        i.push(d.allocationPurposeDesc || "-");
      }

      if (
        !deedType ||
        [
          RULES.RULE0009,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(deedType.root?.rule?.code)
      ) {
        i.push(d.acqusFigDesc || "-");
        i.push(d.dateOfAcquisition || "-");
      }

      if (
        !deedType ||
        [RULES.RULE0009, RULES.RULE0013].includes(deedType.root?.rule?.code)
      ) {
        i.push(d.costPrice || "-");
        i.push(d.marketValue || "-");
        i.push(d.taxValue || "-");
      }

      i.push(d.tokenValue || "-");
      i.push(d.outputShapeDesc || "-");
      i.push(
        d.dateOfOutput ? moment(d.dateOfOutput).format("DD.MM.YYYY") : "-"
      );
      i.push(d.realEstateDesc || "-");

      if (!deedType) {
        i.push(d.durationOfAcquisition || "-");
        i.push(d.administration || "-");
        i.push(d.bkmybsNumber || "-");
        i.push(d.bkmybsName || "-");
        i.push(d.bkmybsRegisterNumber || "-");
        i.push(d.bkmybsAccountingNumber || "-");
        i.push(
          d.bkmybsEntryDate
            ? moment(d.bkmybsEntryDate).format("DD.MM.YYYY")
            : "-"
        );
        i.push(d.province || "-");
        i.push(d.north || "-");
        i.push(d.south || "-");
        i.push(d.east || "-");
        i.push(d.west || "-");
        i.push(d.archiveNumber || "-");
        i.push(
          d.dateOfArchive ? moment(d.dateOfArchive).format("DD.MM.YYYY") : "-"
        );
        i.push(
          d.dateOfRegistration
            ? moment(d.dateOfRegistration).format("DD.MM.YYYY")
            : "-"
        );
      }

      rows.push(i);
    });

    return rows;
  };

  const getExcelFileName = () => {
    switch (deedType?.root?.rule?.code) {
      case RULES.RULE0009:
        return (
          "Taşınmaz Ek 2 Raporları_" + moment(new Date()).format("DD-MM-YYYY")
        );
      case RULES.RULE0010:
        return (
          "Taşınmaz Ek 3 Raporları_" + moment(new Date()).format("DD-MM-YYYY")
        );
      case RULES.RULE0011:
        return (
          "Taşınmaz Ek 4 Raporları_" + moment(new Date()).format("DD-MM-YYYY")
        );
      case RULES.RULE0012:
        return (
          "Taşınmaz Ek 5 Raporları_" + moment(new Date()).format("DD-MM-YYYY")
        );
      case RULES.RULE0013:
        return (
          "Taşınmaz Ek 6 Raporları_" + moment(new Date()).format("DD-MM-YYYY")
        );
      default:
        return "Taşınmaz Raporları_" + moment(new Date()).format("DD-MM-YYYY");
    }
  };

  const getTitle = () => {
    switch (deedType?.root?.rule?.code) {
      case RULES.RULE0009:
        return "Ek-2 Tapuda Kayıtlı Olan Taşınmazlar Raporu";
      case RULES.RULE0010:
        return "Ek-3 Tapuda Kayıtlı Olmayan Taşınmazlar Raporu";
      case RULES.RULE0011:
        return "Ek-4 Orta Malları Raporu";
      case RULES.RULE0012:
        return "Ek-5 Genel Hizmet Alanları Raporu";
      case RULES.RULE0013:
        return "Ek-6 Sınırlı Ayni Haklar İle Kişisel Haklar ve Tahsis Raporu";
      default:
        return "Taşınmaz Raporları";
    }
  };

  return IsAuthorized ? (
    <>
      <PageHeader
        title={getTitle()}
        extra={[
          <DeedTypeSelect
            form={form}
            field="deedType"
            placeholder="Taşınmaz Cinsi ile filtrele"
            allowClear={true}
            style={{ width: "220px", paddingLeft: "16px" }}
          />,
          <ExportExcel
            columnsData={getExcelColumns()}
            key={data}
            disabled={data.length < 1}
            rowsData={getExcelRows()}
            loading={store.process === ACTION_TYPES.FETCHING_DATA}
            filename={getExcelFileName()}
          />,
        ]}
      />
      <Table
        style={{ margin: "0 24px" }}
        rowKey={(row) => row.id}
        loading={process === ACTION_TYPES.FETCHING_DATA}
        dataSource={data}
        columns={getModifiedColumns()}
        size="small"
        bordered
        showSorterTooltip={false}
        scroll={{ x: 1000, scrollToFirstRowOnChange: true }}
        pagination={{
          pageSize: 20,
          total: data.length,
          showSizeChanger: false,
          showTotal: (total, range) => {
            return `Toplam ${total} sonuçtan ${range.join(
              "-"
            )} arası gösteriliyor.`;
          },
        }}
      />
    </>
  ) : (
    <FourOThree />
  );
}
